import Container from "@client/content/Containers/container";
import FormStyleProvider from "@client/content/Providers/FormStyle/container";
import { AccountCardList } from "@client/content/controls/Card/List/component";
import { CurrencyFlagWithLabel } from "@client/content/controls/Flag/component";
import { SPACE_BETWEEN_BUTTONS_STYLE, START_BUTTONS_STYLE } from "@client/content/controls/Form/lib";
import { getContainerProps, getEntityRef, getRootContainerProps } from "@client/content/lib/helper";
import { type ModuleApiInfo } from "@client/content/selectors";
import { SecondaryButton } from "@client/content/shared/Buttons";
import { type RootContainerProps } from "@client/content/types";
import { TransactionActivity } from "@client/controls/Timeline/component";
import { unwrapResponse } from "@client/lib/api";
import { EMPTY_ARRAY } from "@client/lib/array";
import { formatMoneyDisplay } from "@client/lib/format";
import { usePromise } from "@client/lib/hooks";
import { EMPTY_GUID } from "@client/lib/misc";
import { IconType, type OptionItem } from "@client/types";
import appInfo from "@common/appInfo";
import date from "@common/lib/date";
import getOptions from "@common/lib/options";
import StatementModalWithShowSubmit from "@common/modules/account/StatementModal/component";
import * as React from "react";
import CreateUpdateAccountModal from "./CreateUpdateAccountModal/container";
import TransferModal from "./TransferModal/container";
import api from "./api";
import { type MemberWallet } from "./types";

import { useI18n } from "@common/hooks/i18n";
import "./style.less";

export type MapProps = RootContainerProps & {
  accounts: MemberWallet.Account[];
  accountStyle: React.CSSProperties;
  apiInfo: ModuleApiInfo;
  canAdd: boolean;
  currencies: MemberWallet.Currency[];
  detail: MemberWallet.View.Account | null;
  isEditMode: boolean;
  status: string;
  title: string | null;
  userId: number | null;
  columns: number;
  refreshKey: string;
};

export interface ActionProps {
  onAddEditClick: () => void;
  onBackClick: () => void;
  onTransferClick: () => void;
  onItemClick: (item: MemberWallet.Account) => void;
}

const Content = (props: ActionProps & MapProps) => {
  const i18n = useI18n();

  const options = React.useMemo(() => getOptions(i18n), [i18n]);

  const isAddHidden = React.useMemo(
    () =>
      !props.currencies
        .filter((x) => x.isCommission)
        .some((x) => !props.accounts.find((y) => y.currencyCode === x.currencyCode)),
    [props.currencies, props.accounts]
  );

  const accountId = React.useMemo(
    () => props.detail?.accountId ?? props.accounts.find((x) => x.isPreferred)?.accountId,
    [props.detail?.accountId, props.accounts]
  );

  const [monthOptions] = usePromise<OptionItem<string>[]>({
    fn: options.getOptions,
    args: ["bank", "transactions/months", { accountId: accountId }],
    shouldLoad: !props.isEditMode,
    reload: props.refreshKey
  });

  const isStatementEnabled = React.useMemo(
    () => !!(monthOptions ?? EMPTY_ARRAY).filter((x) => !date.isCurrentMonth(x.value)).length,
    [monthOptions]
  );

  if (!props.userId || !appInfo.installedApps.includes("Wallets"))
    return props.isEditMode ? i18n.infoNotInstalled : null;

  return (
    <FormStyleProvider {...getEntityRef(props)}>
      <div className="fc fg">
        {!props.detail && (
          <div className="heading">
            {!!props.title && <span className="title">{props.title}</span>}

            <div className="buttons">
              <SecondaryButton
                icon={IconType.ArrowUp}
                isDisabled={!props.accounts.length}
                onClick={props.onTransferClick}
              >
                {i18n.transfer}
              </SecondaryButton>

              <StatementModalWithShowSubmit
                accountId={accountId!}
                submit={(data) => api.exportTransactions(props.apiInfo, data.accountId, data.date)}
                accountOptions={props.accounts.map((x) => ({
                  value: {
                    accountId: x.accountId,
                    createdOnDate: x.createdOnDate
                  },
                  label: x.currencyCode
                }))}
              >
                {({ show }) => (
                  <SecondaryButton isDisabled={!props.accounts.length} icon={IconType.Download} onClick={show}>
                    {i18n.statement}
                  </SecondaryButton>
                )}
              </StatementModalWithShowSubmit>
            </div>
          </div>
        )}

        {props.detail ? (
          <>
            <div style={SPACE_BETWEEN_BUTTONS_STYLE}>
              <SecondaryButton
                icon={IconType.ArrowLeft}
                isDisabled={!props.accounts.length}
                onClick={props.onBackClick}
              >
                {i18n.back}
              </SecondaryButton>

              <div style={START_BUTTONS_STYLE}>
                <SecondaryButton icon={IconType.ArrowUp} onClick={props.onTransferClick}>
                  {i18n.transfer}
                </SecondaryButton>

                <StatementModalWithShowSubmit
                  accountId={props.detail.accountId}
                  submit={(data) => api.exportTransactions(props.apiInfo, data.accountId, data.date)}
                  accountOptions={EMPTY_ARRAY}
                >
                  {({ show }) => (
                    <SecondaryButton
                      tooltip={isStatementEnabled ? undefined : i18n.infoNoStatementsAvailable}
                      isDisabled={!isStatementEnabled}
                      icon={IconType.Download}
                      onClick={show}
                    >
                      {i18n.statement}
                    </SecondaryButton>
                  )}
                </StatementModalWithShowSubmit>

                <SecondaryButton icon={IconType.Cog} onClick={props.onAddEditClick}>
                  {i18n.settings}
                </SecondaryButton>
              </div>
            </div>

            <div className="gap vertical" />

            <CurrencyFlagWithLabel
              currencyCode={props.detail.currencyCode}
              isRound
              label={formatMoneyDisplay(props.detail.balance, props.detail.currencyCode)}
              size={38}
            />
          </>
        ) : (
          <AccountCardList
            accountStyle={props.accountStyle}
            aspectRatio={1.4}
            addLabel={i18n.addAccount}
            currencies={props.currencies}
            data={props.accounts}
            onAdd={isAddHidden ? undefined : props.canAdd ? props.onAddEditClick : null}
            onClick={props.onItemClick}
            columns={props.columns}
            maxHeight={200}
          />
        )}

        <TransactionActivity
          getItems={async (args) => {
            const resp = await api.getAccountTransactions(props.apiInfo, props.detail?.accountId ?? EMPTY_GUID, args);
            return unwrapResponse(resp);
          }}
          isBorderless
          isNarrow
          refreshKey={props.refreshKey}
          title={i18n.transactions}
        />

        <CreateUpdateAccountModal {...getRootContainerProps(props)} />

        <TransferModal {...getRootContainerProps(props)} />
      </div>
    </FormStyleProvider>
  );
};

export default ({ ...props }: ActionProps & MapProps) => (
  <Container {...getContainerProps(props)} className="wsc_content_member_wallet">
    <Content {...props} />
  </Container>
);
